import type { TypeSelectOption } from '@thefarmersfront/kitchen';

import { DEFAULT_INPUT_STATUS } from '~/constants';
import {
  PRODUCT_DIGIT_LIMIT_FIELDS,
  PRODUCT_MAX_VALUE_FIELDS,
  PRODUCT_POSITIVE_INTEGER_FIELDS,
  PRODUCT_VALIDATION_MESSAGE,
  VOLUME_CALCULATION_FIELDS,
  getDigitLimitFieldsExcludingVolume,
  getRequiredFieldsByOwnerType,
} from '~/constants/product';

import type { TypeInputState } from '~/types/common.types';
import type { TypeProductModalInput, TypeProductModalRequiredInputState } from '~/types/product.types';

/**
 * 값이 비어있는지 확인하는 헬퍼 함수
 */
export const isEmptyField = (
  value: string | number | null | undefined | TypeSelectOption | TypeSelectOption[]
): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && 'value' in value && (!value.value || value.value === '')) return true;
  return false;
};

/**
 * 검증 에러 객체를 생성하는 헬퍼 함수
 */
export const createValidationError = (message: string): TypeInputState => ({
  status: 'error',
  message,
});

/**
 * 상품 등록/상세 모달 > 입력값 검증 메서드 (SKU 코드 검증 제외)
 */
export const validateProductFields = (
  inputValues: TypeProductModalInput,
  ownerType: 'FBK' | '3PL',
  initialStatus: Record<string, TypeInputState>
): Record<string, TypeInputState> => {
  const updatedState: Record<string, TypeInputState> = { ...initialStatus };

  const { selectFields, inputFields } = getRequiredFieldsByOwnerType(ownerType);

  // 1단계: 필수 입력 검증
  [...selectFields, ...inputFields].forEach((field) => {
    if (isEmptyField(inputValues[field])) {
      updatedState[field] = createValidationError(
        (selectFields as string[]).includes(field)
          ? PRODUCT_VALIDATION_MESSAGE.REQUIRED_SELECT
          : PRODUCT_VALIDATION_MESSAGE.REQUIRED_INPUT
      );
    }
  });

  // 상품 운영 종류별, 조건부 필수 입력 필드 검증
  if (inputValues.productType?.value === 'ORIGINAL' && isEmptyField(inputValues.alternativeCode)) {
    updatedState.alternativeCode = createValidationError(PRODUCT_VALIDATION_MESSAGE.REQUIRED_INPUT);
  }

  if (inputValues.productType?.value === 'ALTERNATIVE' && isEmptyField(inputValues.productCode)) {
    updatedState.productCode = createValidationError(PRODUCT_VALIDATION_MESSAGE.REQUIRED_INPUT);
  }

  // 2단계: 최대값 검증
  Object.values(PRODUCT_MAX_VALUE_FIELDS).forEach(({ fields, maxValue, message }) => {
    fields.forEach((field) => {
      const value = inputValues[field];
      if (!isEmptyField(value)) {
        const numValue = Number(value);
        if (!isNaN(numValue) && numValue > maxValue) {
          updatedState[field] = createValidationError(message);
        }
      }
    });
  });

  // 3단계: 자릿수 제한 검증
  Object.values(getDigitLimitFieldsExcludingVolume()).forEach(({ fields, maxDigits, message }) => {
    fields.forEach((field) => {
      const value = inputValues[field];
      if (!isEmptyField(value)) {
        const strValue = String(value);
        if (strValue.replace(/[^\d]/g, '').length > maxDigits) {
          updatedState[field] = createValidationError(message);
        }
      }
    });
  });

  // 4단계: 1 이상의 정수 입력 검증
  PRODUCT_POSITIVE_INTEGER_FIELDS.forEach((field) => {
    const value = inputValues[field];
    if (!isEmptyField(value)) {
      const strValue = String(value);
      const numValue = Number(value);

      if (strValue.includes('.') || (!isNaN(numValue) && numValue < 1)) {
        updatedState[field] = createValidationError(PRODUCT_VALIDATION_MESSAGE.POSITIVE_INTEGER_ONLY);
      }
    }
  });

  // 5단계: 부피 계산 검증
  Object.entries(VOLUME_CALCULATION_FIELDS).forEach(([_, { dimensionFields, volumeField }]) => {
    const hasDimensionErrors = dimensionFields.some((field) => updatedState[field]?.status === 'error');

    if (hasDimensionErrors) return;

    const dimensionValues = dimensionFields.map((field) => Number(inputValues[field]));

    if (dimensionValues.some((val) => isNaN(val) || val <= 0)) return;

    if (!isEmptyField(inputValues[volumeField])) {
      const volumeDigits = String(Math.floor(Number(inputValues[volumeField]))).length;
      if (volumeDigits > PRODUCT_DIGIT_LIMIT_FIELDS.VOLUME.maxDigits) {
        updatedState[volumeField] = createValidationError(PRODUCT_DIGIT_LIMIT_FIELDS.VOLUME.message);
      }
    }
  });

  return updatedState;
};

/**
 * 단일 필드 입력값 검증 함수
 */
export const validateSingleProductField = (
  fieldKey: keyof TypeProductModalRequiredInputState,
  value: string | number | null
): TypeInputState => {
  if (isEmptyField(value)) return DEFAULT_INPUT_STATUS;

  const numValue = Number(value);
  const strValue = String(value);

  // 최대값 검증
  for (const { fields, maxValue, message } of Object.values(PRODUCT_MAX_VALUE_FIELDS)) {
    if (!(fields as readonly string[]).includes(fieldKey)) continue;

    if (!isNaN(numValue) && numValue > maxValue) {
      return createValidationError(message);
    }
  }

  // 자릿수 제한 검증
  const digitLimitFields = getDigitLimitFieldsExcludingVolume();
  for (const { fields, maxDigits, message } of Object.values(digitLimitFields)) {
    if (!(fields as readonly string[]).includes(fieldKey)) continue;

    if (strValue.replace(/[^\d]/g, '').length > maxDigits) {
      return createValidationError(message);
    }
  }

  // 1 이상의 정수 입력 검증
  if ((PRODUCT_POSITIVE_INTEGER_FIELDS as readonly string[]).includes(fieldKey)) {
    if (strValue.includes('.') || (!isNaN(numValue) && numValue < 1)) {
      return createValidationError(PRODUCT_VALIDATION_MESSAGE.POSITIVE_INTEGER_ONLY);
    }
  }

  return DEFAULT_INPUT_STATUS;
};

/**
 * 부피 계산 후 검증 함수
 */
export const validateProductVolumeField = (
  volumeValue: number | null,
  dimensionFieldsStatus: {
    length?: TypeInputState;
    width?: TypeInputState;
    height?: TypeInputState;
  }
): TypeInputState => {
  if (isEmptyField(volumeValue)) return DEFAULT_INPUT_STATUS;

  // 길이, 폭, 높이 필드 중 하나라도 에러 상태이면 기본 상태 반환
  const hasDimensionErrors = Object.values(dimensionFieldsStatus).some(({ status }) => status === 'error');

  if (hasDimensionErrors) return DEFAULT_INPUT_STATUS;

  const volumeDigits = String(volumeValue).length;

  if (volumeDigits > PRODUCT_DIGIT_LIMIT_FIELDS.VOLUME.maxDigits) {
    return createValidationError(PRODUCT_DIGIT_LIMIT_FIELDS.VOLUME.message);
  }

  return DEFAULT_INPUT_STATUS;
};
